// Fill on the glass: a pad that drops one sheep a tap into this llama's jar
// on the screen, a bar held to shut the jar, and nothing that shows the
// count. A llama that is out (or came in late) gets the haunt list: a row
// per jar still in, and a tap on a row drops a ghost in that jar. Then this
// llama's own total and whether it stays, never a table: the standings are
// on the screen. The game's side is `src/party/fill/fill_game.gd`.
//
// A phone's socket may carry about two messages a second, so the pad counts
// taps here, on the room's clock, and sends the running total on a beat. The
// screen meters each batch into the jar, so the jar trails the thumb and the
// count is the player's to keep.

import type { Activity } from '../../protocol';
import { buzz, el } from '../dom';
import { type ListRow, list } from '../list';
import type { Page } from '../page';
import { pixelText } from '../pixelText';
import { say } from '../say';
import { play } from '../sfx';
import { roomTime } from '../state';
import { STRINGS } from '../strings';

// [seat, name, hue] for each jar still in.
type FillJar = [number, string, number];
// [total, verdict]: 0 out, 1 stays, 2 takes a game of the final.
type FillResult = [number, number];
// [place, points] for the knockout's end.
type FillPlace = [number, number];

// The data as the game sends it (`fill_game.gd`, `_data`).
interface FillData {
  round: number;
  final: boolean;
  target: number;
  starts_at: number;
  ends_at: number;
  jars: FillJar[];
  results: Record<string, FillResult>;
  places: Record<string, FillPlace>;
}

// The send beat: under the phone's bucket of two a second.
const SEND_EVERY_MS = 600;
// How long the lock bar is held before the jar shuts.
const LOCK_HOLD_MS = 600;
const VERDICT_OUT = 0;
const VERDICT_FINAL_WIN = 2;
const TARGET_SCALE = 6;
const GOLD = '#e8c170';

const readFill = (activity: Activity): FillData => {
  const data = (activity.data ?? {}) as Partial<FillData>;
  return {
    round: data.round ?? 0,
    final: data.final ?? false,
    target: data.target ?? 0,
    starts_at: data.starts_at ?? 0,
    ends_at: data.ends_at ?? 0,
    jars: Array.isArray(data.jars) ? data.jars : [],
    results: data.results ?? {},
    places: data.places ?? {},
  };
};

// What this phone has done in the round on now. Kept outside the glass, so a
// redraw in the middle of a round keeps the count.
interface Pour {
  key: string;
  drops: number;
  locked: boolean;
  ghosts: Record<string, number>;
  sent: string;
  timer: number | null;
}

let pour: Pour | null = null;

function pourFor(activity: Activity, round: number): Pour {
  const key = `${activity.id}:${round}`;
  if (pour?.key === key) return pour;
  if (pour?.timer != null) window.clearInterval(pour.timer);
  pour = { key, drops: 0, locked: false, ghosts: {}, sent: '', timer: null };
  return pour;
}

// Send the running totals on the beat, only when they changed.
function startSending(page: Page, activity: Activity, current: Pour): void {
  if (current.timer != null) return;
  current.timer = window.setInterval(() => flush(page, activity, current), SEND_EVERY_MS);
}

function flush(page: Page, activity: Activity, current: Pour): void {
  const data = { drops: current.drops, locked: current.locked, ghosts: current.ghosts };
  const text = JSON.stringify(data);
  if (text === current.sent) return;
  current.sent = text;
  page.send({ t: 'play', id: activity.id, data });
}

const isIn = (fill: FillData, seat: number | null): boolean =>
  seat !== null && fill.jars.some((jar) => jar[0] === seat);

// Inside the round's window on the room's clock.
function pouring(page: Page, fill: FillData): boolean {
  const now = roomTime(page.state(), Date.now());
  return now >= fill.starts_at && now < fill.ends_at;
}

export function buildFill(page: Page, activity: Activity): HTMLElement {
  const fill = readFill(activity);
  const seat = page.state().seat;
  const body = el('div', 'body body-fill');
  switch (activity.phase) {
    case 'rules':
      body.append(say(STRINGS.fill.rulesTitle, 'label'));
      for (const line of STRINGS.fill.rules) body.append(say(line));
      return body;
    case 'card':
    case 'countdown':
      body.append(card(fill));
      if (activity.phase === 'countdown') body.append(el('p', 'fill-countdown'));
      return body;
    case 'drop':
      if (isIn(fill, seat)) return pad(page, activity, fill, body);
      return haunt(page, activity, fill, body);
    case 'closed':
      body.append(say(STRINGS.fill.closed, 'label'));
      return body;
    case 'result':
      body.append(result(fill, seat));
      return body;
    case 'places':
      body.append(place(fill, seat));
      return body;
    default:
      body.append(say(STRINGS.fill.watch, 'note'));
      return body;
  }
}

function card(fill: FillData): HTMLElement {
  const box = el('div', 'fill-card');
  box.append(
    say(fill.final ? STRINGS.fill.finalRound(fill.round) : STRINGS.fill.round(fill.round), 'label'),
  );
  box.append(say(STRINGS.fill.collect));
  box.append(pixelText(String(fill.target), 'buzzer', TARGET_SCALE, GOLD, 'fill-target'));
  return box;
}

function pad(page: Page, activity: Activity, fill: FillData, body: HTMLElement): HTMLElement {
  const current = pourFor(activity, fill.round);
  startSending(page, activity, current);
  body.append(say(STRINGS.fill.ask(fill.target)));
  const drop = el('button', 'fill-pad');
  drop.type = 'button';
  drop.setAttribute('aria-label', STRINGS.fill.drop);
  drop.append(el('span', 'fill-pad-sheep'), say(STRINGS.fill.drop, 'label'));
  const lock = el('button', 'fill-lock');
  lock.type = 'button';
  lock.append(el('span', 'fill-lock-fill'), say(STRINGS.fill.hold, 'label'));
  const shut = (): void => {
    body.classList.add('fill-shut');
    drop.disabled = true;
    lock.disabled = true;
    lock.querySelector('.say-label')?.replaceWith(say(STRINGS.fill.locked, 'label'));
  };
  if (current.locked) shut();
  drop.addEventListener('pointerdown', (event) => {
    event.preventDefault();
    if (current.locked || !pouring(page, fill)) return;
    current.drops += 1;
    buzz();
    play('tick');
  });
  holdToLock(lock, () => {
    if (current.locked || !pouring(page, fill)) return;
    current.locked = true;
    buzz(30);
    play('bump');
    flush(page, activity, current);
    shut();
  });
  body.append(drop, lock);
  return body;
}

// The bar fills while held and empties if let go before it is full.
function holdToLock(lock: HTMLButtonElement, onDone: () => void): void {
  let timer: number | null = null;
  const cancel = (): void => {
    if (timer != null) window.clearTimeout(timer);
    timer = null;
    lock.classList.remove('fill-holding');
  };
  lock.addEventListener('pointerdown', (event) => {
    event.preventDefault();
    if (lock.disabled) return;
    lock.classList.add('fill-holding');
    timer = window.setTimeout(() => {
      timer = null;
      lock.classList.remove('fill-holding');
      onDone();
    }, LOCK_HOLD_MS);
  });
  lock.addEventListener('pointerup', cancel);
  lock.addEventListener('pointerleave', cancel);
  lock.addEventListener('pointercancel', cancel);
}

function haunt(page: Page, activity: Activity, fill: FillData, body: HTMLElement): HTMLElement {
  const current = pourFor(activity, fill.round);
  startSending(page, activity, current);
  body.append(say(STRINGS.fill.haunt));
  const rows: ListRow[] = fill.jars.map(([jarSeat, name]) => ({
    name,
    value: STRINGS.fill.ghost,
    onPress: () => {
      if (!pouring(page, fill)) return;
      const key = String(jarSeat);
      current.ghosts[key] = (current.ghosts[key] ?? 0) + 1;
      buzz();
      play('pop');
    },
  }));
  body.append(list(rows, 'fill-haunt'));
  body.append(say(STRINGS.fill.ghostNote, 'note'));
  return body;
}

function result(fill: FillData, seat: number | null): HTMLElement {
  const box = el('div', 'fill-result');
  const mine = seat === null ? undefined : fill.results[String(seat)];
  if (!mine) {
    box.append(say(STRINGS.fill.watch, 'note'));
    return box;
  }
  const [total, verdict] = mine;
  box.append(say(STRINGS.fill.youDropped, 'label'));
  box.append(pixelText(String(total), 'buzzer', TARGET_SCALE, GOLD, 'fill-target'));
  const gap = Math.abs(total - fill.target);
  if (verdict === VERDICT_OUT) box.append(say(STRINGS.fill.out(total, fill.target), 'bad'));
  else if (verdict === VERDICT_FINAL_WIN) box.append(say(STRINGS.fill.tookGame, 'good'));
  else
    box.append(
      say(gap === 0 ? STRINGS.fill.exact : STRINGS.fill.stays(total, fill.target), 'good'),
    );
  return box;
}

function place(fill: FillData, seat: number | null): HTMLElement {
  const box = el('div', 'fill-result');
  const mine = seat === null ? undefined : fill.places[String(seat)];
  if (!mine) {
    box.append(say(STRINGS.fill.watch, 'note'));
    return box;
  }
  const [rank, points] = mine;
  box.append(say(STRINGS.fill.placed, 'label'));
  box.append(pixelText(String(rank), 'buzzer', TARGET_SCALE, GOLD, 'fill-target'));
  box.append(say(STRINGS.fill.points(points), 'good'));
  return box;
}

// The countdown to the chutes opening, in whole seconds on the room's clock.
export function liveFill(page: Page, root: HTMLElement, activity: Activity): void {
  const holder = root.querySelector<HTMLElement>('.fill-countdown');
  if (!holder) return;
  const fill = readFill(activity);
  const left = Math.max(1, Math.ceil((fill.starts_at - roomTime(page.state(), Date.now())) / 1000));
  const text = String(left);
  if (holder.textContent !== text) holder.textContent = text;
}
